import * as actionTypes from '../actionTypes';
import * as eventTypes from '../eventTypes';
import { OFFER_PARTNER_UPDATED } from 'features/offer/edit/client/form/eventTypes';
import { OFFER_ASSET_CLIENT_UPDATED } from 'features/offer/edit/assets/client/eventTypes';
import initialState from 'reducers/initialState';
import * as editableDocument from 'utils/model/editableDocument';
import flow from 'lodash.flow';
import { reduceIsValid } from 'utils/simple-validations/validationInfo';
import { assetHierarchyReducer } from 'features/asset/hierarchy/reducers';
import { getBusinessRules, getValidationRules } from './rules';
import { applyHierarchyRules } from 'features/asset/hierarchy/rules';
import * as hierarchyActionTypes from 'features/asset/hierarchy/actionTypes';
import { evaluatePropertyExpression } from './propertyRules';

const changeAssetProperty = (
  state,
  prop,
  value,
  vatPercent,
  isSecondHand,
  financedAssetDealerWrite,
  popupChassisNumberValidation,
  asset,
  viewType,
  isFacilityFrame = false
) => {
  let { dirtyInfo } = asset;
  const validationRulesFunction = getValidationRules(viewType);
  const result = {
    ...state,
    inEdit: {
      ...state.inEdit,
      asset: flow(
        editableDocument.update(prop, value, getBusinessRules(vatPercent, !isSecondHand)),
        editableDocument.validate(
          validationRulesFunction(
            financedAssetDealerWrite,
            isFacilityFrame,
            dirtyInfo?.chassisNumber ? dirtyInfo.chassisNumber : false,
            popupChassisNumberValidation,
            isSecondHand,
            prop === 'isChassisNumberAvailable' ? value : state.inEdit.asset.document.isChassisNumberAvailable,
            state.inEdit.secondHandProperties[state.inEdit.asset.document.itemId]?.hasWorkingHours,
            prop === 'partner.supplierType' ? value : state.inEdit.asset?.document?.partner?.supplierType
          ),
          true
        )
      )(asset)
    }
  };
  return result;
};

function offerAssetDataReducer(state = initialState.currentOffer.assetData, action) {
  switch (action.type) {
    case actionTypes.INIT_ASSET_DATA_STARTED:
      return {
        ...state,
        initializing: true
      };

    case actionTypes.INIT_ASSET_DATA_COMPLETED:
      return {
        ...state,
        initializing: false
      };

    case actionTypes.SET_ASSET_IN_EDIT:
      return {
        ...state,
        inEdit: {
          ...initialState.currentOffer.assetData.inEdit,
          asset: editableDocument.create(action.payload.asset),
          retrievingAssetData: state.inEdit.retrievingAssetData,
          isLoaded: true
        }
      };

    case actionTypes.SET_ASSET_DIRTY:
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          asset: editableDocument.setDirty(state.inEdit.asset)
        }
      };

    case OFFER_ASSET_CLIENT_UPDATED:
    case OFFER_PARTNER_UPDATED: {
      if (action.type === OFFER_PARTNER_UPDATED && action.event.eventBody.isAssociatedPartner) return state;

      const shouldUpdateInEdit = state.inEdit.isLoaded && !state.inEdit.asset.document.clientSelected;
      const shouldUpdateAssets = state.assets.some(asset => !asset.clientSelected);
      if (!shouldUpdateInEdit && !shouldUpdateAssets) return state;

      return {
        ...state,
        inEdit: shouldUpdateInEdit
          ? {
              ...state.inEdit,
              asset: {
                ...state.inEdit.asset,
                document: {
                  ...state.inEdit.asset.document,
                  clientSelected: true
                }
              }
            }
          : state.inEdit,
        assets: shouldUpdateAssets
          ? Object.assign(
              state.assets.map(asset => (asset.clientSelected ? asset : { ...asset, clientSelected: true })),
              { isLoaded: state.assets.isLoaded }
            )
          : state.assets
      };
    }

    case actionTypes.LOAD_ASSET_DATA_STARTED:
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          retrievingAssetData: true
        }
      };

    case actionTypes.LOAD_ASSET_DATA_FINISHED:
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          retrievingAssetData: false
        }
      };

    case actionTypes.DELETE_OFFER_ASSET_STARTED:
      return {
        ...state,
        deleting: true
      };

    case actionTypes.DELETE_OFFER_ASSET_FAILED:
      return {
        ...state,
        deleting: false
      };

    case actionTypes.DELETE_OFFER_ASSET_SUCCEEDED: {
      return initialState.currentOffer.assetData;
    }

    case actionTypes.LOAD_OFFER_ASSET_SUCCEEDED: {
      const assets = Object.assign(
        [...state.assets.filter(x => x.documentDetailId !== action.payload.asset.documentDetailId), action.payload.asset],
        {
          isLoaded: state.assets.isLoaded
        }
      );

      return {
        ...state,
        assets
      };
    }

    case actionTypes.LOAD_OFFER_ASSETS_SUCCEEDED: {
      return {
        ...state,
        assets: Object.assign([], action.payload.assets, { isLoaded: true }),
        inEdit: {
          ...state.inEdit,
          isLoaded: false
        }
      };
    }

    case actionTypes.LOAD_OFFER_ASSETS_FAILED: {
      return {
        ...state,
        assets: Object.assign([], { isLoaded: false }),
        inEdit: initialState.currentOffer.assetData.inEdit
      };
    }

    case actionTypes.LOAD_OFFER_ASSETS_HEADER_SUCCEEDED: {
      return {
        ...state,
        assetsHeader: Object.assign([], action.payload.assets, { isLoaded: true })
      };
    }

    case actionTypes.LOAD_OFFER_ASSETS_HEADER_FAILED: {
      return {
        ...state,
        assetsHeader: Object.assign([], { isLoaded: false })
      };
    }

    case actionTypes.UPDATE_OFFER_ASSET_CHASSIS_NO_STARTED: {
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          updatingChassisNumber: true
        }
      };
    }

    case actionTypes.UPDATE_OFFER_ASSET_CHASSIS_CANCELED: {
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          asset: flow(
            editableDocument.update('chassisNumber', action.payload, null),
            editableDocument.resetDirtyInfo,
            editableDocument.resetValidationInfo
          )(state.inEdit.asset)
        }
      };
    }

    case actionTypes.RESET_DIRTY_INFO: {
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          asset: flow(editableDocument.resetDirtyInfo, editableDocument.resetValidationInfo)(state.inEdit.asset)
        }
      };
    }

    case actionTypes.UPDATE_OFFER_ASSET_CHASSIS_NUMBER_SUCCESS: {
      const { chassisNumber, documentDetailId } = action.payload;
      return {
        ...state,
        assets: state.assets.map(asset =>
          asset.documentDetailId === documentDetailId
            ? {
                ...asset,
                chassisNumber: chassisNumber,
                isChassisNumberAvailable: true
              }
            : asset
        ),
        inEdit: {
          ...state.inEdit,
          updatingChassisNumber: false,
          asset: flow(editableDocument.resetDirtyInfo, editableDocument.resetValidationInfo)(state.inEdit.asset)
        }
      };
    }

    case hierarchyActionTypes.SET_ASSET_HIERARCHY_LEVELS: {
      const { id, levels, viewType } = action.payload;
      const { assetTypeId } = state.inEdit.asset.document;
      if (id !== assetTypeId) return state;

      const { asset } = state.inEdit;
      const { isSecondHand } = state.inEdit.asset.document;
      const { financedAssetDealerWrite, vatPercent } = action.payload.bag;

      return changeAssetProperty(state, 'hierarchy', levels, vatPercent, isSecondHand, financedAssetDealerWrite, false, asset, viewType);
    }

    case actionTypes.CHANGE_ASSET_PROPERTY: {
      let { prop, value, isSecondHand, viewType } = action.payload;
      const {
        vatPercent,
        financedAssetDealerWrite,
        popupChassisNumberValidation,
        isFacilityFrame,
        assetTypesContextFormula,
        assetTypeCodes
      } = action.payload;
      let { asset } = state.inEdit;

      if (prop === 'assetTypeId' || prop === 'hierarchyLevel') {
        let dirtyInfo = { ...asset.dirtyInfo };
        delete dirtyInfo['properties'];
        let validationInfo = { ...asset.validationInfo, isValid: true };
        delete validationInfo['properties'];

        asset = { ...asset, dirtyInfo: dirtyInfo, validationInfo: validationInfo };
        asset = { ...asset, validationInfo: { ...validationInfo, isValid: reduceIsValid(asset.validationInfo) } };
      }

      if (prop === 'isChassisNumberAvailable') {
        const shouldResetQuantity = value === true;
        asset = {
          ...asset,
          document: { ...asset.document, isSecondHand, quantity: shouldResetQuantity ? 1 : asset.document.quantity },
          dirtyInfo: { ...asset.dirtyInfo, chassisNumber: true, quantity: shouldResetQuantity }
        };
      }

      if (prop === 'category') {
        isSecondHand = value === 'Used' || value === 'Ex-Demo';
        asset = {
          ...asset,
          document: {
            ...asset.document,
            isSecondHand,
            km: !isSecondHand ? 0 : asset.document.km,
            workingHours: !isSecondHand ? 0 : asset.document.workingHours
          },
          dirtyInfo: { ...asset.dirtyInfo, isSecondHand }
        };
      }

      if (prop === 'isSecondHand' && (value === true || value === false)) isSecondHand = value;

      if (isSecondHand)
        asset = {
          ...asset,
          dirtyInfo: { ...asset.dirtyInfo, km: prop !== 'km', workingHours: prop !== 'workingHours' }
        };

      if (prop === 'hierarchyLevel') {
        value = applyHierarchyRules(asset.document.hierarchy, value, isFacilityFrame);
        prop = 'hierarchy';
      }

      if ((prop === 'acquisitionPriceWithoutVat' || prop === 'acquisitionPriceWithVat') && assetTypesContextFormula && assetTypeCodes) {
        let result = changeAssetProperty(
          state,
          prop,
          value,
          vatPercent,
          isSecondHand,
          financedAssetDealerWrite,
          popupChassisNumberValidation,
          asset,
          viewType,
          isFacilityFrame
        );
        const { properties, assetTypeId } = asset.document;

        properties?.forEach((property, index) => {
          assetTypesContextFormula.forEach(contextFormula => {
            if (contextFormula.propertyId !== property.propertyId) return;

            contextFormula.rules.forEach(rule => {
              const newValue = evaluatePropertyExpression(rule, assetTypeCodes, {
                value,
                assetTypeId
              });
              if (property.value === newValue) return;
              if (!newValue) return;

              result = changeAssetProperty(
                result,
                `properties.${index}.value`,
                newValue,
                vatPercent,
                isSecondHand,
                financedAssetDealerWrite,
                popupChassisNumberValidation,
                result.inEdit.asset,
                viewType,
                isFacilityFrame
              );
            });
          });
        });
        return result;
      }

      return changeAssetProperty(
        state,
        prop,
        value,
        vatPercent,
        isSecondHand,
        financedAssetDealerWrite,
        popupChassisNumberValidation,
        asset,
        viewType,
        isFacilityFrame
      );
    }

    case actionTypes.VALIDATE_ASSET: {
      const {
        financedAssetDealerWrite,
        isSecondHand,
        isFacilityFrame,
        viewType,
        mandatoryPropertiesConfigured,
        initialProperties = []
      } = action.payload;
      const validationRulesFunction = getValidationRules(viewType);

      const { asset } = state.inEdit;

      const updatedProperties = isFacilityFrame
        ? []
        : asset.document.properties?.map(property => {
            return {
              ...property,
              mandatory: mandatoryPropertiesConfigured?.includes(property.propertyCode)
                ? true
                : initialProperties.find(p => p.propertyCode === property.propertyCode)?.mandatory ?? property.mandatory
            };
          });
      const validatedAsset = editableDocument.validate(
        validationRulesFunction(
          financedAssetDealerWrite,
          isFacilityFrame,
          false,
          false,
          isSecondHand,
          false,
          state.inEdit.secondHandProperties[state.inEdit.asset.document.itemId]?.hasWorkingHours,
          state.inEdit.asset?.document?.partner?.supplierType
        ),
        false,
        {
          ...state.inEdit.asset,
          document: {
            ...state.inEdit.asset.document,
            properties: updatedProperties
          }
        }
      );

      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          asset: validatedAsset
        }
      };
    }

    case actionTypes.SAVE_OFFER_ASSET_STARTED:
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          saving: true
        }
      };

    case actionTypes.SAVE_OFFER_ASSET_FAILED:
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          saving: false
        }
      };

    case actionTypes.SAVE_OFFER_ASSET_SUCCEEDED:
      return initialState.currentOffer.assetData;

    case actionTypes.DUPLICATE_OFFER_ASSET_STARTED:
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          duplicating: true
        }
      };

    case actionTypes.DUPLICATE_OFFER_ASSET_FAILED:
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          duplicating: false
        }
      };

    case actionTypes.DUPLICATE_OFFER_ASSET_SUCCEEDED:
      return initialState.currentOffer.assetData;

    // revert changes action
    case actionTypes.CANCEL_OFFER_ASSET_CHANGES:
      return {
        ...state,
        inEdit: initialState.currentOffer.assetData.inEdit
      };

    case eventTypes.OFFER_ASSET_INVALIDATED: {
      return {
        ...state,
        assets: Object.assign([], { isLoaded: false })
      };
    }

    case actionTypes.LOAD_SECOND_HAND_PROPERTIES_VISIBILITY_SUCCEEDED: {
      const { itemId, hasWorkingHours, defaultValue } = action.payload;
      return {
        ...state,
        inEdit: {
          ...state.inEdit,
          secondHandProperties: {
            ...state.inEdit.secondHandProperties,
            [itemId]: { ...state.inEdit.secondHandProperties[itemId], hasWorkingHours, defaultValue }
          }
        }
      };
    }

    default:
      return state;
  }
}

export default function offerAssetReducer(state = initialState.currentOffer.assetData, action) {
  const offerAssetDataState = offerAssetDataReducer(state, action);
  const assetHierarchyState = assetHierarchyReducer(offerAssetDataState.inEdit.hierarchy, action);
  return {
    ...offerAssetDataState,
    inEdit: {
      ...offerAssetDataState.inEdit,
      hierarchy: {
        ...assetHierarchyState
      }
    }
  };
}
